import React, { JSX, useState } from "react";
import { Box, Button, Tab, Tabs } from "@mui/material";

import startCase from "lodash/startCase";
import produce from "immer";
import Panel from "../../../../components/Panel";
import SettingsButton from "../../../../components/SettingsButton/SettingsButton";
import MedicalReportSettings from "./MedicalReportSettings";
import AssessmentMedicalVitals from "../AssessmentMedicalVitals";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import { ResourceCentre } from "../../../../interfaces/ResourceCentreInterface";
import OkhatiDialog from "../../../../components/Dialog/Dialog";
import { updateUserResourceCentre } from "../../../../actions/resourceCentre";
import { notificationAdd } from "../../../../actions/notification";
import { resourceCentreActions as actions } from "../../../../actions";
import { useAppDispatch } from "../../../../store/hooks";

interface Props {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  renderSettingButton?: boolean;
}
enum TAB_ITEMS {
  MEDICAL_SETTINGS = "medical_settings",
  GLOBAL_SETTINGS = "global_settings"
}

const MedicalSettingsPanel = ({
  open,
  onOpenChange,
  renderSettingButton = true
}: Props): JSX.Element => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [tab, setTab] = React.useState(TAB_ITEMS.MEDICAL_SETTINGS);
  const resourceCentre = useCurrentResourceCentre();
  const [rc, setRc] = useState(resourceCentre);
  const isControlled = typeof open === "boolean";
  const actualOpen = isControlled ? open : internalOpen;
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [vitalSettings, setVitalSettings] = useState(rc.settings.vitalSettings);
  const [vitalsOrder, setVitalsOrder] = useState<Array<string>>(
    rc.settings.assessmentSettings?.vitalsOrder
  );
  const [loading, setLoading] = useState(false);

  const setOpen = (value: boolean): void => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  };
  const dispatch = useAppDispatch();

  return (
    <>
      {renderSettingButton && <SettingsButton onClick={() => setOpen(!actualOpen)} />}

      {actualOpen && (
        <Panel
          onClose={() => setOpen(false)}
          title="Medical Settings"
          footer={
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                width: "100%"
              }}
            >
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => {
                  setShowConfirmDialog(true);
                }}
              >
                Save
              </Button>
            </Box>
          }
          contentStyle={{ height: "calc(100vh - 100px)", py: 0 }}
        >
          <Box sx={{ borderLeft: 1, borderRight: 1, borderColor: "divider" }}>
            <Tabs
              value={tab}
              onChange={(_, newValue) => setTab(newValue)}
              sx={{
                height: "50px",
                borderBottom: 1,
                borderColor: "divider"
              }}
            >
              <Tab
                value={TAB_ITEMS.MEDICAL_SETTINGS}
                label={startCase(TAB_ITEMS.MEDICAL_SETTINGS)}
              />
              <Tab value={TAB_ITEMS.GLOBAL_SETTINGS} label={startCase(TAB_ITEMS.GLOBAL_SETTINGS)} />
            </Tabs>

            <Box sx={{ height: "calc(100vh - 150px)", p: 2, overflowY: "auto" }}>
              {tab === TAB_ITEMS.MEDICAL_SETTINGS && (
                <MedicalReportSettings setRc={setRc} rc={rc} />
              )}
              {tab === TAB_ITEMS.GLOBAL_SETTINGS && (
                <AssessmentMedicalVitals
                  vitalSettings={vitalSettings}
                  setVitalSettings={setVitalSettings}
                  vitalsOrder={vitalsOrder}
                  setVitalsOrder={setVitalsOrder}
                />
              )}
            </Box>
          </Box>

          {showConfirmDialog && (
            <OkhatiDialog
              title="Change medical report settings ?"
              description="Your changes will be permanently saved."
              readMode={false}
              isLoading={loading}
              cancel={() => {
                setShowConfirmDialog(false);
              }}
              next={async () => {
                try {
                  setLoading(true);
                  const newRc = produce(rc, (draft: ResourceCentre) => {
                    draft.settings = {
                      ...draft.settings,
                      vitalSettings,
                      assessmentSettings: {
                        ...draft.settings.assessmentSettings,
                        vitalsOrder
                      }
                    };
                  });
                  await dispatch(actions.putResourceCentre(newRc));
                  setRc(newRc);
                  updateUserResourceCentre(newRc);
                  setShowConfirmDialog(false);
                  dispatch(
                    notificationAdd({
                      id: new Date().getUTCMilliseconds(),
                      variant: "success",
                      message: "Assessment Labels updated successfully.",
                      autoTimeout: true
                    })
                  );
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (e: any) {
                  dispatch(
                    notificationAdd({
                      id: new Date().getUTCMilliseconds(),
                      variant: "error",
                      message: e?.message || e?.data?.message || "Something went wrong",
                      autoTimeout: true
                    })
                  );
                } finally {
                  setLoading(false);
                }
              }}
            />
          )}
        </Panel>
      )}
    </>
  );
};
export default MedicalSettingsPanel;
